import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VIEWABLE_MIME_BY_EXTENSION = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  txt: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
};

const VIEWABLE_TYPES = new Set(Object.values(VIEWABLE_MIME_BY_EXTENSION));

const getExtension = (fileName = '') => {
  const cleanName = String(fileName).split('?')[0].split('#')[0];
  const parts = cleanName.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

export const inferPreviewMimeType = (fileName, mimeType) => {
  const normalizedType = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (normalizedType && normalizedType !== 'application/octet-stream') {
    return normalizedType;
  }
  return VIEWABLE_MIME_BY_EXTENSION[getExtension(fileName)] || normalizedType || 'application/octet-stream';
};

export const preparePreviewBlob = (blob, fileName) => {
  const type = inferPreviewMimeType(fileName, blob?.type);
  if (blob?.type === type) return { blob, type };
  return { blob: new Blob([blob], { type }), type };
};

export const canPreviewInBrowser = (fileName, mimeType) => VIEWABLE_TYPES.has(inferPreviewMimeType(fileName, mimeType));

export default function FilePreviewModal({ isOpen, fileName = 'arquivo', loadFile, onClose, onDownload }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [objectUrl, setObjectUrl] = useState('');
  const [mimeType, setMimeType] = useState('');
  const [unsupported, setUnsupported] = useState(false);
  const objectUrlRef = useRef('');

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setError('');
      setObjectUrl('');
      setMimeType('');
      setUnsupported(false);
      return undefined;
    }

    let mounted = true;
    const controller = new AbortController();

    const revokeCurrentUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
    };

    setLoading(true);
    setError('');
    setObjectUrl('');
    setMimeType('');
    setUnsupported(false);
    revokeCurrentUrl();

    Promise.resolve()
      .then(() => loadFile?.({ signal: controller.signal }))
      .then((result) => {
        if (!mounted) return;
        const rawBlob = result?.data || result?.blob || result;
        if (!rawBlob) throw new Error('Arquivo não encontrado.');
        const responseType = result?.headers?.['content-type'] || result?.type || rawBlob.type;
        const inferredType = inferPreviewMimeType(fileName, responseType);
        if (!canPreviewInBrowser(fileName, inferredType)) {
          setUnsupported(true);
          setMimeType(inferredType);
          return;
        }
        const prepared = rawBlob.type === inferredType ? rawBlob : new Blob([rawBlob], { type: inferredType });
        const nextUrl = URL.createObjectURL(prepared);
        objectUrlRef.current = nextUrl;
        setMimeType(inferredType);
        setObjectUrl(nextUrl);
      })
      .catch((err) => {
        if (!mounted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        console.error('Erro ao visualizar arquivo', err);
        setError('Não foi possível carregar este arquivo.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      controller.abort();
      revokeCurrentUrl();
    };
  }, [isOpen, fileName, loadFile]);

  const preview = useMemo(() => {
    if (!objectUrl) return null;
    if (mimeType === 'application/pdf') {
      return <iframe title={fileName} src={objectUrl} className="h-full min-h-[60vh] w-full rounded-xl border border-slate-200" />;
    }
    if (mimeType.startsWith('image/')) {
      return <img src={objectUrl} alt={fileName} className="max-h-[70vh] max-w-full rounded-xl object-contain" />;
    }
    return <iframe title={fileName} src={objectUrl} className="h-full min-h-[60vh] w-full rounded-xl border border-slate-200 bg-white" />;
  }, [fileName, mimeType, objectUrl]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="file-preview-title">
      <div className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Visualização de arquivo</p>
            <h2 id="file-preview-title" className="truncate text-lg font-bold text-slate-900" title={fileName}>{fileName}</h2>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onDownload} className="rounded-lg border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50">Baixar arquivo</button>
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Fechar</button>
          </div>
        </header>
        <main className="flex min-h-[50vh] flex-1 items-center justify-center overflow-auto bg-slate-50 p-4 sm:p-6">
          {loading && <p className="text-sm font-semibold text-slate-600">Carregando arquivo...</p>}
          {!loading && error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
          {!loading && unsupported && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm font-semibold text-amber-800">Este formato não possui visualização no navegador.</div>}
          {!loading && !error && !unsupported && preview}
        </main>
      </div>
    </div>,
    document.body
  );
}
